import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/Hooks/useAuth';
import { usePermission } from '@/Hooks/usePermission';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useAppDispatch } from '@/Redux/store';
import { useConfirm } from '@/components/ui/confirm';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { VENUE_EVENTS, rooms, type Branch, type MenuVersionEventPayload } from '@/lib/shared-types';
import {
  formatDateTime,
  fromDatetimeLocal,
  isCancellable,
  isEditableStatus,
  isRollbackable,
  personName,
} from './lib';
import { refusalOf, refusalKey } from './lib/refusals';
import {
  menuVersionApprovePath,
  menuVersionCancelPath,
  menuVersionPath,
  menuVersionPublishPath,
  menuVersionRejectPath,
  menuVersionRollbackPath,
  menuVersionSubmitPath,
} from './menuGovernanceRoutes';
import { invalidateMenuVersion, useMenuVersionQuery } from './menuGovernanceApi';
import RuleEditor from './components/RuleEditor';
import ConflictPreview from './components/ConflictPreview';

/**
 * The version, opened in full: header facts, the rules editor (screen 2),
 * review & approve (screen 3, with the authorship separation surfaced),
 * the conflict preview (screen 4) and schedule/publish/cancel/rollback
 * (screen 5) — every facet of one version in one place, the shape
 * `Pages/Billing/Checks.tsx` opens `CheckDetail` in rather than a route of
 * its own.
 */
export default function VersionDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { can } = usePermission();
  const { confirm, prompt } = useConfirm();

  const { data: version, isLoading, isError, refetch } = useMenuVersionQuery(id);

  const [busy, setBusy] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');
  const [publishAt, setPublishAt] = useState('');
  const [publishNote, setPublishNote] = useState('');
  const [editingMeta, setEditingMeta] = useState(false);

  const refresh = () => {
    dispatch(invalidateMenuVersion(id));
    refetch();
    onChanged();
  };

  /**
   * The six events `RealtimeService.onMenuVersionEvent` fans out to every
   * branch a version targets — see `VersionsPanel.tsx` for the wire's own
   * shape. Scoped to THIS version's own target branches (not the header's
   * active branch, `useRealtime`'s default) because a version being watched
   * here may target a branch nobody has selected up top, and refetched only
   * when `payload.versionId` names this version — the same branch room
   * carries every OTHER version's traffic too, and a lifecycle change on one
   * of those is not a reason to reload this dialog.
   *
   * Called with hooks unconditionally, before the loading/error early
   * returns below — `version` is undefined on the first render and the
   * `?? []` keeps the room list empty rather than throwing, so the six calls
   * stay in the same position on every render the rules of hooks require.
   */
  const versionRooms = useMemo(
    () => version?.targets.map((tg) => rooms.branch(tg.branchId)) ?? [],
    [version],
  );
  const onVersionEvent = (payload: MenuVersionEventPayload) => {
    if (payload.versionId === id) refresh();
  };
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_SUBMITTED, onVersionEvent, versionRooms);
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_APPROVED, onVersionEvent, versionRooms);
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_REJECTED, onVersionEvent, versionRooms);
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_SCHEDULED, onVersionEvent, versionRooms);
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_PUBLISHED, onVersionEvent, versionRooms);
  useRealtime<MenuVersionEventPayload>(VENUE_EVENTS.MENU_VERSION_ROLLED_BACK, onVersionEvent, versionRooms);
  useRealtimeResync(refresh);

  const run = async (action: () => Promise<unknown>, successKey: string) => {
    setBusy(true);
    try {
      await action();
      toast.success(t(successKey));
      refresh();
    } catch (error) {
      const refusal = refusalOf(error);
      toast.error(t(refusalKey(refusal)));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog open onClose={onClose} title={t('common.loading')} className="max-w-5xl">
        <LoadingState />
      </Dialog>
    );
  }

  if (isError || !version) {
    return (
      <Dialog open onClose={onClose} title={t('menuGovernance.detail.notFound')} className="max-w-xl">
        <ErrorState message={t('menuGovernance.detail.notFound')} onRetry={refetch} />
      </Dialog>
    );
  }

  const canDraft = can('menu_governance.draft');
  const canApprove = can('menu_governance.approve');
  const canPublish = can('menu_governance.publish');
  const canRollback = can('menu_governance.rollback');
  const mayApproveOwn = Boolean(user?.isPlatformAdmin) || can('menu_governance.approve_own');
  const isOwn = version.createdById === user?.id;

  const editable = isEditableStatus(version.status) && canDraft;
  const inReview = version.status === 'IN_REVIEW';
  const approved = version.status === 'APPROVED';
  const cancellable = isCancellable(version) && canPublish;
  const rollbackable = isRollbackable(version) && canRollback;

  /** Mirrors `ApprovalDetail`'s `standing()` — the one sentence that explains the buttons. */
  const reviewStanding = (): string | null => {
    if (!canApprove) return t('menuGovernance.review.notPermitted');
    if (isOwn && !mayApproveOwn) return t('menuGovernance.review.selfBlocked');
    return null;
  };
  const standing = inReview ? reviewStanding() : null;

  const submit = () => run(() => api.post(menuVersionSubmitPath(id)), 'menuGovernance.toast.submitted');
  const approve = () =>
    run(
      () => api.post(menuVersionApprovePath(id), { note: decisionNote.trim() || undefined }),
      'menuGovernance.toast.approved',
    );
  const reject = () =>
    run(
      () => api.post(menuVersionRejectPath(id), { note: decisionNote.trim() || undefined }),
      'menuGovernance.toast.rejected',
    );

  const doPublish = async () => {
    const iso = fromDatetimeLocal(publishAt);
    const ok = await confirm({
      title: t('menuGovernance.publish.publishConfirmTitle'),
      body: iso
        ? t('menuGovernance.publish.publishConfirmBodyScheduled', {
            time: formatDateTime(iso, i18n.language),
          })
        : t('menuGovernance.publish.publishConfirmBodyNow'),
      confirmLabel: iso ? t('menuGovernance.publish.publishScheduled') : t('menuGovernance.publish.publishNow'),
    });
    if (!ok) return;
    run(
      () =>
        api.post(menuVersionPublishPath(id), {
          effectiveFrom: iso ?? undefined,
          note: publishNote.trim() || undefined,
        }),
      iso ? 'menuGovernance.toast.scheduled' : 'menuGovernance.toast.published',
    );
  };

  const doCancel = async () => {
    const ok = await confirm({
      title: t('menuGovernance.publish.cancelConfirmTitle'),
      body: t('menuGovernance.publish.cancelConfirmBody'),
      confirmLabel: t('menuGovernance.publish.cancel'),
      tone: 'danger',
    });
    if (!ok) return;
    run(() => api.post(menuVersionCancelPath(id)), 'menuGovernance.toast.cancelled');
  };

  const doRollback = async () => {
    const note = await prompt({
      title: t('menuGovernance.rollback.confirmTitle', { versionNo: version.versionNo }),
      body: t('menuGovernance.rollback.confirmBody', { versionNo: version.versionNo }),
      label: t('menuGovernance.rollback.note'),
      confirmLabel: t('menuGovernance.rollback.submit'),
      tone: 'danger',
    });
    if (note === null) return;
    run(
      () => api.post(menuVersionRollbackPath(id), { note: note.trim() || undefined }),
      'menuGovernance.toast.rolledBack',
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`#${version.versionNo} — ${version.name}`}
      description={version.notes ?? undefined}
      className="max-w-5xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('menuGovernance.detail.close')}
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <dl className="grid flex-1 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Fact label={t('menuGovernance.versions.columnStatus')}>
              <Badge tone={toneForStatus(version.status)}>{t(`menuGovernance.status.${version.status}`)}</Badge>
            </Fact>
            <Fact label={t('menuGovernance.detail.targets')}>
              {version.targets.map((tg) => tg.branch.name).join(', ') || '—'}
            </Fact>
            <Fact label={t('menuGovernance.detail.createdBy')}>
              {personName(version.createdBy)} · {formatDateTime(version.createdAt, i18n.language)}
            </Fact>
            {version.submittedAt && (
              <Fact label={t('menuGovernance.detail.submittedAt')}>
                {formatDateTime(version.submittedAt, i18n.language)}
              </Fact>
            )}
            {version.approvedBy && (
              <Fact label={t('menuGovernance.detail.approvedBy')}>
                {personName(version.approvedBy)} · {formatDateTime(version.approvedAt, i18n.language)}
              </Fact>
            )}
            {version.publishedBy && (
              <Fact label={t('menuGovernance.detail.publishedBy')}>
                {personName(version.publishedBy)} · {formatDateTime(version.publishedAt, i18n.language)}
              </Fact>
            )}
            {version.effectiveFrom && (
              <Fact label={t('menuGovernance.detail.effectiveFrom')}>
                {formatDateTime(version.effectiveFrom, i18n.language)}
              </Fact>
            )}
            {version.rolledBackAt && (
              <Fact label={t('menuGovernance.detail.rolledBackAt')}>
                {formatDateTime(version.rolledBackAt, i18n.language)}
              </Fact>
            )}
            {version.reverts && (
              <Fact label={t('menuGovernance.detail.revertsOfLabel')}>
                {`#${version.reverts.versionNo} — ${version.reverts.name}`}
              </Fact>
            )}
          </dl>
          {editable && (
            <Button variant="outline" size="sm" onClick={() => setEditingMeta(true)}>
              {t('menuGovernance.detail.editName')}
            </Button>
          )}
        </div>

        {version.status === 'REJECTED' && version.decisionNote && (
          <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200">
            {t('menuGovernance.review.rejectedNote', { note: version.decisionNote })}
          </p>
        )}

        <RuleEditor version={version} editable={editable} onChanged={refresh} />

        {version.status === 'DRAFT' && canDraft && (
          <div className="flex items-center gap-3 border-t pt-4">
            <Button onClick={submit} disabled={busy || version.rules.length === 0}>
              {t('menuGovernance.review.submit')}
            </Button>
            {version.rules.length === 0 && (
              <p className="text-sm text-ink-muted">{t('menuGovernance.review.submitEmptyBlocked')}</p>
            )}
          </div>
        )}

        {inReview && (
          <section className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold">{t('menuGovernance.detail.sectionReview')}</h3>
            {/*
              `VERSION_INCLUDE` expands `createdBy` / `approvedBy` / `publishedBy`
              into `{ id, firstName, lastName }` but leaves `submittedBy`
              unexpanded — only `submittedById` and `submittedAt` travel — so
              there is no name to put in a "submitted by" sentence here without
              a second `GET /users/:id`. The facts grid above still shows
              *when* it was submitted; naming *who* is left out rather than
              guessed at from `createdBy`, since submitting only requires
              `menu_governance.draft` and is not always the same person who
              opened the draft.
            */}
            <Field label={t('menuGovernance.review.decisionNote')}>
              <Textarea
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                placeholder={t('menuGovernance.review.decisionNotePlaceholder')}
                rows={2}
                disabled={!!standing}
              />
            </Field>
            {standing && (
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                {standing}
                {isOwn && canApprove && !mayApproveOwn && (
                  <span className="mt-1 block text-xs">{t('menuGovernance.review.selfBlockedOwnHint')}</span>
                )}
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="danger" disabled={busy || !!standing} onClick={reject}>
                {t('menuGovernance.review.reject')}
              </Button>
              <Button disabled={busy || !!standing} onClick={approve}>
                {t('menuGovernance.review.approve')}
              </Button>
            </div>
          </section>
        )}

        <div className="border-t pt-4">
          <ConflictPreview versionId={id} effectiveFrom={version.effectiveFrom} />
        </div>

        {approved && canPublish && (
          <section className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold">{t('menuGovernance.detail.sectionPublish')}</h3>
            <Field
              label={t('menuGovernance.publish.effectiveFrom')}
              hint={t('menuGovernance.publish.publishHint')}
            >
              <Input
                type="datetime-local"
                value={publishAt}
                onChange={(e) => setPublishAt(e.target.value)}
              />
            </Field>
            <Field label={t('menuGovernance.publish.note')}>
              <Textarea value={publishNote} onChange={(e) => setPublishNote(e.target.value)} rows={2} />
            </Field>
            <Button onClick={doPublish} disabled={busy}>
              {publishAt ? t('menuGovernance.publish.publishScheduled') : t('menuGovernance.publish.publishNow')}
            </Button>
          </section>
        )}

        {(cancellable || rollbackable) && (
          <div className="flex flex-wrap items-center gap-3 border-t pt-4">
            {cancellable && (
              <Button variant="outline" disabled={busy} onClick={doCancel}>
                {t('menuGovernance.publish.cancel')}
              </Button>
            )}
            {rollbackable && (
              <div className="flex items-center gap-2">
                <Button variant="danger" disabled={busy} onClick={doRollback}>
                  {t('menuGovernance.rollback.button')}
                </Button>
                <p className="text-xs text-ink-muted">{t('menuGovernance.rollback.explain')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {editingMeta && (
        <EditVersionMetaDialog
          version={version}
          onClose={() => setEditingMeta(false)}
          onSaved={() => {
            setEditingMeta(false);
            refresh();
          }}
        />
      )}
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function EditVersionMetaDialog({
  version,
  onClose,
  onSaved,
}: {
  version: { id: string; name: string; notes: string | null; targets: { branchId: string }[] };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [name, setName] = useState(version.name);
  const [notes, setNotes] = useState(version.notes ?? '');
  const [branchIds, setBranchIds] = useState<string[]>(version.targets.map((tg) => tg.branchId));
  const [saving, setSaving] = useState(false);

  const canReadBranches = can('branches.view');
  const branchesQuery = useApi<unknown>(canReadBranches ? '/branches' : null, { limit: 200 });
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const toggle = (id: string) =>
    setBranchIds((cur) => (cur.includes(id) ? cur.filter((b) => b !== id) : [...cur, id]));

  const save = async () => {
    if (!name.trim() || branchIds.length === 0) return;
    setSaving(true);
    try {
      await api.patch(menuVersionPath(version.id), {
        name: name.trim(),
        notes: notes.trim() || null,
        branchIds,
      });
      toast.success(t('menuGovernance.toast.updated'));
      onSaved();
    } catch {
      toast.error(t('menuGovernance.refusal.genericBody'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('menuGovernance.detail.editName')}
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('menuGovernance.editor.cancel')}
          </Button>
          <Button onClick={save} disabled={saving || !name.trim() || branchIds.length === 0}>
            {t('menuGovernance.detail.saveDetails')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('menuGovernance.versions.createName')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('menuGovernance.versions.createNotes')}>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Field>
        <Field label={t('menuGovernance.versions.createBranches')} required>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2">
            {branches.map((b) => (
              <label key={b.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-hover">
                <input
                  type="checkbox"
                  checked={branchIds.includes(b.id)}
                  onChange={() => toggle(b.id)}
                  className="h-4 w-4 shrink-0 accent-[var(--accent-solid)]"
                />
                <span className="text-sm">{b.name}</span>
              </label>
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
